// React and Hooks
import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import Cookies from 'js-cookie'

// Icons
import { MdOutlineDelete, MdOpenInNew } from 'react-icons/md'
import { CiEdit } from 'react-icons/ci'
import { FaImage, FaLink } from 'react-icons/fa'

// Components
import Button from '../Custom/Buttons/Button'
import CustomToast from '../Custom/CustomToast'

// Utils and Hooks
import axiosInterceptorInstance from '../../utils/axios/axiosInterceptorInstance'
import { useDebounce } from '../../hooks/useDebounce'
import configManager from '../../config/configManager'

// Assets
import brokenImg from '../../assets/images/brokenImg.png'

// Constants
const TEXTURE_CATEGORIES = [
    { value: null, label: 'Select Category', disabled: true, selected: true },
    { value: 'Stone', label: 'Stone' },
    { value: 'Brick', label: 'Brick' },
    { value: 'Wood', label: 'Wood' },
    { value: 'Terrazzo', label: 'Terrazzo' },
    { value: 'Concrete', label: 'Concrete' },
    { value: 'Metal', label: 'Metal' },
    { value: 'Tile', label: 'Tile' },
    { value: 'Fabric', label: 'Fabric' },
    { value: 'Wallpaper', label: 'Wallpaper' },
    { value: 'Carpet', label: 'Carpet' },
    { value: 'Surfaces', label: 'Surfaces' },
    { value: 'Finishes', label: 'Finishes' },
    { value: 'Landscaping', label: 'Landscaping' },
    { value: 'Insulation', label: 'Insulation' },
    { value: 'Organic', label: 'Organic' },
    { value: 'Acoustic', label: 'Acoustic' },
]

const ITEMS_PER_PAGE = 50

const SeamlessTextures = ({ userPermissions = [] }) => {
    const configData = configManager()
    const [loading, setLoading] = useState(false)
    const [category, setSelectedCategory] = useState(null)
    const [page, setPage] = useState(1)
    const [search, setSearch] = useState('')
    const [textures, setTextures] = useState([])
    const [moreAvailable, setMoreAvailable] = useState(true)
    const [deleteTextures, setDeleteTextures] = useState([])
    const [isDeleting, setIsDeleting] = useState(false)

    const debouncedSearchTerm = useDebounce(search, 300)

    useEffect(() => {
        setPage(1)
        setTextures([])
        fetchTextures({
            page: 1,
            limit: ITEMS_PER_PAGE,
            category: category === 'Select Category' ? null : category,
            search: debouncedSearchTerm,
        })
    }, [debouncedSearchTerm, category])

    const fetchTextures = async (params) => {
        const urlParams = new URLSearchParams({
            page: params.page || 1,
            limit: params.limit || ITEMS_PER_PAGE,
            front: false,
            ...(params.category && { category: params.category }),
            ...(params.search && { search: params.search }),
        })

        setLoading(true)
        try {
            const { data } = await axiosInterceptorInstance.post(
                `/seamless-textures?${urlParams.toString()}`,
                { body: JSON.stringify(params) },
            )

            if (data?.status === 'success') {
                setTextures((prev) =>
                    params.page === 1
                        ? data.results || []
                        : [...prev, ...(data.results || [])],
                )
                setMoreAvailable(!!data.more)
            } else {
                CustomToast({
                    message: 'Failed to fetch seamless textures',
                    type: 'error',
                })
            }
        } catch (error) {
            CustomToast({
                message: 'An error occurred while fetching seamless textures',
                type: 'error',
            })
        } finally {
            setLoading(false)
        }
    }

    const handleDeleteTextures = async () => {
        if (!userPermissions.includes('seamless-texture:delete')) {
            CustomToast({
                message: 'You do not have permission to delete seamless textures',
                type: 'error',
            })
            return
        }

        if (!deleteTextures.length) {
            CustomToast({
                message: 'Please select textures to delete',
                type: 'warning',
            })
            return
        }

        // ids snapshot — finally se pehle filter ke liye
        const idsToDelete = [...deleteTextures]

        setIsDeleting(true)
        try {
            const response = await axiosInterceptorInstance.delete(
                '/seamless-textures/delete',
                {
                    data: { deleteTextures: idsToDelete },
                },
            )

            const res = response?.data
            const ok =
                res?.success === true ||
                res?.status === 'success' ||
                res?.status === true ||
                response?.status === 200

            if (ok) {
                // 1) UI se turant hatao
                setTextures((prev) =>
                    prev.filter((texture) => {
                        const id = texture._id || texture.id
                        return !idsToDelete.includes(id)
                    }),
                )

                // 2) selection clear
                setDeleteTextures([])

                CustomToast({
                    message: 'Seamless textures deleted successfully',
                    type: 'success',
                })

                // 3) server se list refresh (pagination / more flag sync)
                setPage(1)
                await fetchTextures({
                    page: 1,
                    limit: ITEMS_PER_PAGE,
                    category: category === 'Select Category' ? null : category,
                    search: debouncedSearchTerm,
                })
            } else {
                CustomToast({
                    message: res?.message || 'Failed to delete seamless textures',
                    type: 'error',
                })
            }
        } catch (error) {
            console.error('Delete seamless textures error:', error)
            CustomToast({
                message:
                    error?.response?.data?.message ||
                    'Failed to delete seamless textures. Please try again',
                type: 'error',
            })
        } finally {
            setIsDeleting(false)
        }
    }

    const handleLoadMore = () => {
        const nextPage = page + 1
        setPage(nextPage)
        fetchTextures({
            page: nextPage,
            limit: ITEMS_PER_PAGE,
            category: category === 'Select Category' ? null : category,
            search: debouncedSearchTerm,
        })
    }

    const handleSelectForDelete = (textureId) => {
        if (!userPermissions.includes('seamless-texture:delete')) return

        setDeleteTextures((prev) =>
            prev.includes(textureId)
                ? prev.filter((id) => id !== textureId)
                : [...prev, textureId],
        )
    }

    const handleSelectAllForDelete = () => {
        if (!userPermissions.includes('seamless-texture:delete')) return

        setDeleteTextures((prev) =>
            prev.length === textures.length
                ? []
                : textures.map((texture) => texture._id),
        )
    }

    const getBrandName = (brand) => {
        if (!brand) return null
        if (typeof brand === 'string') return brand
        if (typeof brand === 'object') {
            return brand.name || brand._id || null
        }
        return null
    }

    /** Create editor link for this seamless texture */
    const getEditorLink = (texture) => {
        const id = texture._id || texture.id
        const token = Cookies.get('admin_token') || ''
        const params = new URLSearchParams({ seamless_id: id })
        if (token) params.set('admin_token', token)
        return `${configData.BACKEND_URL}/create?${params.toString()}`
    }

    const handleCopyEditorLink = async (texture, e) => {
        e.preventDefault()
        e.stopPropagation()
        const link = configData.BACKEND_URL + `/create?seamless_id=${texture._id}`
        try {
            await navigator.clipboard.writeText(link)
            CustomToast({ message: 'Editor link copied', type: 'success' })
        } catch (err) {
            // fallback
            const input = document.createElement('input')
            input.value = link
            document.body.appendChild(input)
            input.select()
            document.execCommand('copy')
            document.body.removeChild(input)
            CustomToast({ message: 'Editor link copied', type: 'success' })
        }
    }

    const getThumbnailSrc = (texture) => {
        if (!texture.thumbnail) return brokenImg
        const path = texture.thumbnail.startsWith('/')
            ? texture.thumbnail.slice(1)
            : `thumbnails/${texture.thumbnail}`
        return `${configData.CDN_BASE_URL}/${path}`
    }

    const renderDeleteControls = () => {
        if (
            !deleteTextures.length ||
            !userPermissions.includes('seamless-texture:delete')
        ) {
            return null
        }

        return (
            <>
                <Button
                    label='Delete'
                    disabled={isDeleting}
                    isLoading={isDeleting}
                    onClick={handleDeleteTextures}
                    className='flex gap-1.5 items-center font-medium rounded-lg disabled:pointer-events-none disabled:opacity-70 border-2 !border-red-700 hover:!bg-red-700 !text-red-700 hover:!text-white'
                >
                    <MdOutlineDelete className='text-2xl' />
                </Button>
                <label className='flex gap-2 items-center font-semibold'>
                    <input
                        className='w-5 h-5'
                        type='checkbox'
                        checked={deleteTextures.length === textures.length}
                        onChange={handleSelectAllForDelete}
                    />
                    Select All
                </label>
            </>
        )
    }

    return (
        <div>
            <div className='flex gap-4 justify-start items-center flex-wrap'>
                {renderDeleteControls()}
                <div className='ml-auto'>
                    <select
                        className='w-full border rounded-lg px-3 py-3 focus:outline-none focus:ring-2 focus:ring-prime'
                        value={category || ''}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        {TEXTURE_CATEGORIES.map((cat) => (
                            <option
                                key={cat.value || 'default'}
                                value={cat.value || ''}
                                disabled={cat.disabled}
                            >
                                {cat.label}
                            </option>
                        ))}
                    </select>
                </div>
                <input
                    placeholder='Search seamless textures...'
                    className='w-full sm:w-[300px] border-[2px] border-prime rounded-lg p-3 focus:outline-none focus:border-orange'
                    type='text'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </div>

            {loading && (
                <p className='text-center my-20'>Loading seamless textures...</p>
            )}

            {!loading && textures.length === 0 && (
                <p className='text-center my-10'>No seamless textures available.</p>
            )}

            <div className='grid grid-cols-2 sm:grid-cols-4 md:grid-cols-5 justify-around gap-4 my-8'>
                {textures.map((texture) => (
                    <div key={texture._id} className='m-4 text-center'>
                        <div className='relative group w-36 h-36 mx-auto'>
                            {/* Open in Create Editor — new tab */}
                            {/* <a
                                href={getEditorLink(texture)}
                                target='_blank'
                                rel='noopener noreferrer'
                                title='Open in Create Editor'
                                className='absolute top-2 left-2 z-10 bg-white/95 p-1.5 text-sm hidden group-hover:inline-flex items-center rounded-full shadow text-prime hover:bg-prime hover:text-white'
                                onClick={(e) => e.stopPropagation()}
                            >
                                <MdOpenInNew className='text-lg' />
                            </a> */}

                            {/* Copy editor link */}
                            <button
                                type='button'
                                title='Copy editor link'
                                className='absolute top-2 left-2 z-10 bg-white/95 p-1.5 text-sm hidden group-hover:inline-flex items-center rounded-full shadow text-prime hover:bg-prime hover:text-white'
                                onClick={(e) => handleCopyEditorLink(texture, e)}
                            >
                                <FaLink className='text-sm' />
                            </button>

                            {userPermissions.includes('seamless-texture:update') && (
                                <Link
                                    to={`/seamless-texture/update/${texture._id}`}
                                    className='absolute bottom-2 right-2 z-10 bg-prime p-2 text-xl hidden text-white rounded-full group-hover:inline-block'
                                >
                                    <CiEdit />
                                </Link>
                            )}

                            {texture.thumbnail ? (
                                <img
                                    className={`w-full h-full object-cover bg-gray-50 ${deleteTextures?.includes(texture._id)
                                        ? 'border-4 border-prime'
                                        : ''
                                        }`}
                                    src={getThumbnailSrc(texture)}
                                    onError={(e) => (e.target.src = brokenImg)}
                                    alt={texture.name || 'Seamless Texture'}
                                    onClick={() =>
                                        userPermissions.includes('seamless-texture:delete')
                                            ? handleSelectForDelete(texture._id)
                                            : null
                                    }
                                />
                            ) : (
                                <div
                                    className={`w-full h-full bg-gray-100 flex items-center justify-center ${deleteTextures?.includes(texture._id)
                                        ? 'border-4 border-prime'
                                        : ''
                                        }`}
                                    onClick={() =>
                                        userPermissions.includes('seamless-texture:delete')
                                            ? handleSelectForDelete(texture._id)
                                            : null
                                    }
                                >
                                    <FaImage className='text-4xl text-gray-400' />
                                </div>
                            )}

                            {userPermissions.includes('seamless-texture:delete') && (
                                <input
                                    type='checkbox'
                                    className={`w-4 h-4 absolute top-4 right-4 z-10 ${deleteTextures?.includes(texture._id) ? 'block' : 'hidden'
                                        }`}
                                    checked={deleteTextures?.includes(texture._id)}
                                    onChange={() => handleSelectForDelete(texture._id)}
                                />
                            )}
                        </div>

                        <div className='py-3 px-2'>
                            {texture.name && (
                                <div className='font-semibold'>{texture.name}</div>
                            )}
                            {texture.category && (
                                <div className='text-gray-600'>{texture.category}</div>
                            )}
                            {getBrandName(texture.brand) && (
                                <div className='text-gray-500 text-sm'>
                                    {getBrandName(texture.brand)}
                                </div>
                            )}

                            {/* Always-visible small open link (optional) */}
                            <a
                                href={getEditorLink(texture)}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='inline-flex items-center gap-1 mt-1 text-xs text-prime hover:underline'
                            >
                                Open in Editor <MdOpenInNew />
                            </a>
                        </div>
                    </div>
                ))}
            </div>

            {moreAvailable && !loading && (
                <div className='flex justify-center mt-8'>
                    <Button
                        label='Load More'
                        onClick={handleLoadMore}
                        className='bg-prime text-white px-6 py-2 rounded-lg hover:bg-prime/90'
                    />
                </div>
            )}
        </div>
    )
}

export default SeamlessTextures